import { Box, CircularProgress, Grid, useMediaQuery } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import AdminCalendarPanel from "./AdminCalendarPanel";
import AdminDetailsPanel from "./AdminDetailsPanel";
import ConfirmationModal from "./ConfirmationModal";
import NotificationSnackbar from "./NotificationSnackbar";
import { useTheme } from "@emotion/react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";

import { formatLocalDateKey } from "utils/dateUtils";
import { isWithinInterval } from "date-fns";
import { db } from "../../../firebase";

const CalendarView = () => {
  // Core data states
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [bookedDateRanges, setBookedDateRanges] = useState([]);
  const [availableWeekdays, setAvailableWeekdays] = useState({});

  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [showDateDetails, setShowDateDetails] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  // Refs and responsive
  const detailsRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Global confirmation action state - to be consumed by ConfirmationModal
  const [globalConfirmAction, setGlobalConfirmAction] = useState(null);

  // Fetch all calendar data from Firestore
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch unavailable dates
        const unavailableSnap = await getDocs(
          collection(db, "bus_unavailable_dates")
        );
        setUnavailableDates(unavailableSnap.docs.map((doc) => doc.id));

        // Fetch bookings with date ranges
        const bookingsSnap = await getDocs(collection(db, "bus_bookings"));
        const bookingRanges = [];

        bookingsSnap.forEach((doc) => {
          const bookingData = doc.data();

          // Handle both new startDate/endDate model and legacy single date model
          if (bookingData.startDate && bookingData.endDate) {
            // New model with date range
            const startDate = bookingData.startDate.toDate
              ? bookingData.startDate.toDate()
              : new Date(bookingData.startDate);

            const endDate = bookingData.endDate.toDate
              ? bookingData.endDate.toDate()
              : new Date(bookingData.endDate);

            bookingRanges.push({
              id: doc.id,
              startDate: formatLocalDateKey(startDate),
              endDate: formatLocalDateKey(endDate),
              bookingData: { ...bookingData, id: doc.id },
            });
          } else if (bookingData.date) {
            // Legacy model with single date
            const dateObj = bookingData.date.toDate
              ? bookingData.date.toDate()
              : new Date(bookingData.date);

            const dateKey = formatLocalDateKey(dateObj);
            bookingRanges.push({
              id: doc.id,
              startDate: dateKey,
              endDate: dateKey, // Same date for both start and end
              bookingData: { ...bookingData, id: doc.id },
            });
          }
        });

        setBookedDateRanges(bookingRanges);

        // Fetch available weekdays
        const weekdaysSnap = await getDoc(
          doc(db, "bus_available_weekdays", "bus_available_weekdays")
        );
        if (weekdaysSnap.exists()) {
          setAvailableWeekdays(weekdaysSnap.data());
        }
      } catch (error) {
        console.error("Error fetching calendar data:", error);
        setSnackbar({
          open: true,
          message: "Erreur lors du chargement des données",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle date selection in calendar
  const handleDateSelect = (date) => {
    const dateKey = formatLocalDateKey(date);
    setSelectedDateStr(dateKey);

    // Reset booking details
    setBookingDetails(null);

    // Find booking details if date is within a booking range
    const dateObj = new Date(date);
    dateObj.setHours(12, 0, 0, 0); // Normalize to noon

    const matchingBooking = bookedDateRanges.find((range) => {
      if (!range.startDate || !range.endDate) return false;

      // Create normalized date objects for comparison
      const startDate = new Date(range.startDate);
      startDate.setHours(12, 0, 0, 0);

      const endDate = new Date(range.endDate);
      endDate.setHours(12, 0, 0, 0);

      return isWithinInterval(dateObj, {
        start: startDate,
        end: endDate,
      });
    });

    if (matchingBooking) {
      // Set booking details directly from our cached data
      setBookingDetails(matchingBooking.bookingData);
    }

    // Show detail view
    setShowDateDetails(true);

    // Scroll to details on mobile devices after animation
    if (isMobile) {
      setTimeout(() => {
        if (detailsRef.current) {
          detailsRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 600); // Wait for animation to complete
    }
  };

  // Close detail view
  const handleCloseDetails = () => {
    setShowDateDetails(false);
    setSelectedDateStr(null);
  };

  return (
    <Grid container spacing={{ xs: 2, md: 3 }}>
      <Grid item xs={12} md={6}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 20,
              alignItems: "center",
            }}
          >
            <CircularProgress color="error" />
          </Box>
        ) : (
          <AdminCalendarPanel
            unavailableDates={unavailableDates}
            bookedDateRanges={bookedDateRanges}
            availableWeekdays={availableWeekdays}
            onDateSelect={handleDateSelect}
            setShowDateDetails={setShowDateDetails}
            setSelectedDateStr={setSelectedDateStr}
          />
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <AdminDetailsPanel
          showDateDetails={showDateDetails}
          selectedDateStr={selectedDateStr}
          bookedDateRanges={bookedDateRanges}
          unavailableDates={unavailableDates}
          bookingDetails={bookingDetails}
          setBookingDetails={setBookingDetails}
          setBookedDateRanges={setBookedDateRanges}
          detailsRef={detailsRef}
          availableWeekdays={availableWeekdays}
          setAvailableWeekdays={setAvailableWeekdays}
          setUnavailableDates={setUnavailableDates}
          setGlobalConfirmAction={setGlobalConfirmAction}
          handleCloseDetails={handleCloseDetails}
          setSnackbar={setSnackbar}
        />
      </Grid>
      <ConfirmationModal
        globalConfirmAction={globalConfirmAction}
        setGlobalConfirmAction={setGlobalConfirmAction}
      />

      <NotificationSnackbar snackbar={snackbar} setSnackbar={setSnackbar} />
    </Grid>
  );
};

export default CalendarView;
